"use client"

import { useEffect } from "react"
import { usePathname } from "next/navigation"
import { toast } from "sonner"
import { useTranslations } from "next-intl"
import { useAuthStore } from "@/lib/auth-store"
import { useModalSlot } from "@/lib/client/modal-coordinator"
import { GooncoinAddedPopup } from "./gooncoin-added-popup"

/** Mounted once, globally (see AuthProvider) — shows whenever the fire-and-
 * forget daily-login check in auth-store's refreshProfile() actually awarded
 * Gooncoin. Clearing the store field on close is what prevents it from
 * reopening on the next render. Takes its turn through the shared popup
 * coordinator so it never opens on top of another popup.
 *
 * A brand-new account's very first claim (balance == the amount just awarded)
 * is awarded quietly with a small toast instead of a popup: the first-visit
 * Early Access / Glymtober cards already explain the daily bonus.
 *
 * Only opens on the home page (owner, 2026-10-19). On any other page the claim
 * waits in the store and the popup shows the next time they land on "/". */
export function DailyGooncoinClaimPopup() {
  const t = useTranslations("gooncoinAddedPopup")
  const claim = useAuthStore((state) => state.dailyGooncoinClaim)
  const setDailyGooncoinClaim = useAuthStore((state) => state.setDailyGooncoinClaim)
  const onHome = usePathname() === "/"
  const firstEverClaim = !!claim && claim.newBalance === claim.amount
  const canShow = useModalSlot("dailyClaim", onHome && !!claim && !firstEverClaim)

  useEffect(() => {
    if (!claim || !firstEverClaim) return
    toast.success(t("gooncoinAdded", { amount: claim.amount }), { description: t("dailyLoginBonus") })
    setDailyGooncoinClaim(null)
  }, [claim, firstEverClaim, setDailyGooncoinClaim, t])

  if (!claim || firstEverClaim || !onHome) return null

  return (
    <GooncoinAddedPopup
      open={canShow}
      onOpenChange={(open) => { if (!open) setDailyGooncoinClaim(null) }}
      amount={claim.amount}
      newBalance={claim.newBalance}
      source="daily"
    />
  )
}
